/**
 * RegisterVisitorDrawer — V1.5 (Dynamic Field Registry)
 *
 * Three-step visitor registration flow:
 *
 *   Step 1: WorkflowTypeSelector
 *     Large tap-target tiles — one per active VisitorPurpose.
 *
 *   Step 2: DynamicVisitorForm
 *     Field set driven by the org's enabled field schema (Phase 4).
 *     Field configuration is per-org, not per-purpose.
 *
 *   Step 3: PhotoCaptureStep  (optional — skippable)
 *     Webcam preview → capture → upload to POST /visitors/:id/photo/.
 *
 * Preserved behaviour:
 *   - Phone blur → duplicate check → DuplicateWarning
 *   - "Log visit" → POST /visitors/:id/log-visit/
 *   - Normal submit → POST /visitors/ → advance to photo step
 */
import { useEffect, useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { WorkflowTypeSelector } from './WorkflowTypeSelector'
import { DynamicVisitorForm, type VisitorFormValues } from './DynamicVisitorForm'
import PhotoCaptureStep from './PhotoCaptureStep'
import {
  useVisitorPurposes,
  useFieldSchema,
  useCheckPhone,
  useCreateVisitor,
  useLogVisit,
} from '../hooks'
import type { Visitor, WorkflowType } from '@/types'

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RegisterVisitorDrawerProps {
  open:         boolean
  onOpenChange: (open: boolean) => void
  onSuccess?:   () => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RegisterVisitorDrawer({
  open,
  onOpenChange,
  onSuccess,
}: RegisterVisitorDrawerProps) {
  // ── Step management ───────────────────────────────────────────────────────
  const [step, setStep] = useState<'select' | 'form' | 'photo'>('select')

  // ── Selection / registration state ───────────────────────────────────────
  const [selectedPurposeId, setSelectedPurposeId]   = useState<string | null>(null)
  const [registeredVisitor, setRegisteredVisitor]   = useState<Visitor | null>(null)

  // ── Duplicate detection ───────────────────────────────────────────────────
  const [duplicateVisitor, setDuplicateVisitor] = useState<Visitor | null>(null)
  const [dismissedDuplicate, setDismissedDuplicate] = useState(false)

  // ── Fast-track: pre-fill values from returning visitor ────────────────────
  const [prefillValues, setPrefillValues] = useState<Record<string, string> | undefined>(
    undefined,
  )

  // ── Data hooks ─────────────────────────────────────────────────────────────
  const { data: purposes = [] } = useVisitorPurposes()

  // Phase 4: field schema is org-wide (not per-purpose), fetched once for all steps
  const { data: fieldSchema, isLoading: isLoadingSchema } = useFieldSchema()

  const checkPhone    = useCheckPhone()
  const createVisitor = useCreateVisitor()
  const logVisit      = useLogVisit(duplicateVisitor?.id ?? '')

  // ── Reset on close ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) {
      setStep('select')
      setSelectedPurposeId(null)
      setRegisteredVisitor(null)
      setDuplicateVisitor(null)
      setDismissedDuplicate(false)
      setPrefillValues(undefined)
    }
  }, [open])

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleTypeSelect = (purposeId: string, _workflowType: WorkflowType) => {
    setSelectedPurposeId(purposeId)
    setStep('form')
  }

  const handleBack = () => {
    setStep('select')
    setDuplicateVisitor(null)
    setDismissedDuplicate(false)
  }

  const handlePhoneBlur = async (phone: string) => {
    if (!phone || phone.length < 7) return
    setDuplicateVisitor(null)
    setDismissedDuplicate(false)
    setPrefillValues(undefined)
    try {
      const result = await checkPhone.mutateAsync(phone)
      if (result.found && result.visitor) {
        const v = result.visitor
        setDuplicateVisitor(v)
        // Fast-track: build a string-keyed prefill map from the returning visitor.
        // phone_number is already in the field so we skip it here (it's the trigger).
        const extra: Record<string, string> = v.extra_data
          ? Object.fromEntries(
              Object.entries(v.extra_data as Record<string, unknown>).map(([k, val]) => [
                k,
                String(val ?? ''),
              ]),
            )
          : {}
        setPrefillValues({
          first_name: v.first_name ?? '',
          last_name:  v.last_name  ?? '',
          email:      v.email      ?? '',
          notes:      v.notes      ?? '',
          gender:     v.gender     ?? '',
          address:    v.address    ?? '',
          date_of_birth: v.date_of_birth ?? '',
          ...extra,
        })
      }
    } catch {
      // Silently ignore — never block registration on a check error
    }
  }

  const handleLogRepeatVisit = async () => {
    if (!duplicateVisitor) return
    await logVisit.mutateAsync({})
    onOpenChange(false)
    onSuccess?.()
  }

  const handleSubmit = async (values: VisitorFormValues) => {
    // Build payload from all visible form values; backend routes model vs extra_data fields
    const visitor = await createVisitor.mutateAsync({
      first_name:   String(values['first_name']   ?? ''),
      last_name:    String(values['last_name']    ?? ''),
      phone_number: String(values['phone_number'] ?? ''),
      email:        values['email'] ? String(values['email']) : undefined,
      purpose_id:   selectedPurposeId ?? undefined,
      notes:        values['notes'] ? String(values['notes']) : undefined,
      // Extra-data fields (Phase 4 — Dynamic Field Registry)
      ...(values['company']            && { company:            String(values['company'])            }),
      ...(values['host_name']          && { host_name:          String(values['host_name'])          }),
      ...(values['host_phone']         && { host_phone:         String(values['host_phone'])         }),
      ...(values['vehicle_reg']        && { vehicle_reg:        String(values['vehicle_reg'])        }),
      ...(values['parcel_description'] && { parcel_description: String(values['parcel_description']) }),
      ...(values['position_applied']   && { position_applied:   String(values['position_applied'])   }),
    })
    // Advance to photo step — visitor is now created
    setRegisteredVisitor(visitor)
    setStep('photo')
  }

  const handlePhotoComplete = () => {
    onOpenChange(false)
    onSuccess?.()
  }

  const handlePhotoSkip = () => {
    onOpenChange(false)
    onSuccess?.()
  }

  // ── Duplicate state (passed to form) ─────────────────────────────────────

  const duplicateInfo = duplicateVisitor && !dismissedDuplicate
    ? {
        visitor:    duplicateVisitor,
        onLogVisit: () => { void handleLogRepeatVisit() },
        onDismiss:  () => { setDismissedDuplicate(true) },
        isLogging:  logVisit.isPending,
      }
    : null

  // ── Step metadata ─────────────────────────────────────────────────────────

  const stepTitle =
    step === 'select' ? 'Register Visitor'
    : step === 'form' ? 'Visitor Details'
    : 'Capture Photo'

  const stepDescription =
    step === 'select' ? 'Select the type of visit to get started.'
    : step === 'form' ? 'Complete the visitor information below.'
    : 'Take a photo for identification (optional).'

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col sm:max-w-lg overflow-y-auto"
        style={{ backgroundColor: '#f9f9f6', color: '#000000' }}
      >
        <SheetHeader className="pb-4">
          <SheetTitle style={{ color: '#000000' }}>{stepTitle}</SheetTitle>
          <SheetDescription style={{ color: '#000000' }}>
            {stepDescription}
          </SheetDescription>
        </SheetHeader>

        {step === 'select' && (
          <>
            <WorkflowTypeSelector
              purposes={purposes}
              selectedPurposeId={selectedPurposeId}
              onSelect={handleTypeSelect}
            />
            <div className="mt-auto pt-6">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="w-full rounded-lg border-2 py-3 text-sm font-medium opacity-60 hover:opacity-100 transition-opacity"
                style={{ color: '#000000', borderColor: 'rgba(0,0,0,0.2)' }}
              >
                Cancel
              </button>
            </div>
          </>
        )}

        {step === 'form' && (
          <DynamicVisitorForm
            fields={fieldSchema ?? null}
            isLoadingConfig={isLoadingSchema}
            onPhoneBlur={(phone) => { void handlePhoneBlur(phone) }}
            duplicate={duplicateInfo}
            prefillValues={prefillValues}
            onSubmit={handleSubmit}
            isSubmitting={createVisitor.isPending}
            onCancel={() => onOpenChange(false)}
            onBack={handleBack}
            formId="register-visitor-form"
          />
        )}

        {step === 'photo' && registeredVisitor && (
          <PhotoCaptureStep
            visitorId={registeredVisitor.id}
            visitorName={registeredVisitor.full_name || registeredVisitor.first_name}
            onComplete={handlePhotoComplete}
            onSkip={handlePhotoSkip}
          />
        )}
      </SheetContent>
    </Sheet>
  )
}
